import { useState, useCallback } from 'react'
import { Space, Typography, Tooltip, Row, Col, Modal as ModalAntd, notification } from 'antd'
import { DeleteOutlined, LeftOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import _ from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { useAsync } from 'react-use'
import { useStoreState, useStoreActions } from 'easy-peasy'
import update from 'immutability-helper'
import { v4 as uuidv4 } from 'uuid'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Input from 'components/Input'
import Select from 'components/Select'
import InputNumber from 'components/InputNumber'
import AllActiveInactive from 'components/AllActiveInactive'
import ReportPreview from 'components/ReportPreview'
import FilterGroupAttributeSelect from 'components/FilterGroupAttributeSelect'
import FilterDataTypeSelect from 'components/FilterDataTypeSelect'

import { useTranslate } from 'hooks/translate'

import { getReportTypeByIdApi } from 'services/reportType'
import {
  getReportTypeAttributeListApi,
  createReportTypeAttributeApi,
  deleteReportTypeAttributeByIdApi,
  updateReportTypeAttributeByIdApi,
} from 'services/reportType/attribute'
import { getDictionaryValueFilterListApi } from 'services/filter/dictionary/value'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function ReportAttributeDetail () {
  const { t } = useTranslate()
  const history = useHistory()
  const { id } = useParams()

  const [reportType, setReportType] = useState({})
  const [oldDataSource, setOldDataSource] = useState([])
  const [currentDataSource, setCurrentDataSource] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    group_attribute_id: null,
    data_type_id: null,
    key_attribute: null,
    param: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive
  const [submitting, setSubmitting] = useState(false)
  // const [editingId, setEditingId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showReportPreview, setShowReportPreview] = useState(false)
  const [dataForCreate, setDataForCreate] = useState({})
  const [dataForUpdate, setDataForUpdate] = useState({})
  const [allDictionaryValues, setAllDictionaryValues] = useState({})

  const groupAttributes = useStoreState((state) => state.filter.groupAttributes)
  const dataTypes = useStoreState((state) => state.filter.dataTypes)
  const dictionaries = useStoreState((state) => state.filter.dictionaries)
  const days = useStoreState((state) => state.filter.days)
  const reportTypeAttributes = useStoreState((state) => state.filter.reportTypeAttributes)
  const reportStatuses = useStoreState((state) => state.filter.reportStatuses)

  const getGroupAttributes = useStoreActions((actions) => actions.filter.getGroupAttributes)
  const getDataTypes = useStoreActions((actions) => actions.filter.getDataTypes)
  const getDictionaries = useStoreActions((actions) => actions.filter.getDictionaries)
  const getDays = useStoreActions((actions) => actions.filter.getDays)
  const getReportTypeAttributes = useStoreActions(
    (actions) => actions.filter.getReportTypeAttributes
  )
  const getReportStatuses = useStoreActions((actions) => actions.filter.getReportStatuses)

  const numberDataTypes = _.filter(dataTypes, (dataType) => {
    return _.includes(['integer', 'float'], dataType?.name)
  })

  const numberDataTypesIds = _.map(numberDataTypes, (numberDataType) => numberDataType?.id)

  const getReportTypeById = async (id) => {
    const res = await getReportTypeByIdApi(id)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setReportType(res?.data)
  }

  const _list = async () => {
    if (loading) return

    const params = {
      report_type_id: id,
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getReportTypeAttributeListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setOldDataSource(res.data?.items)
    setCurrentDataSource(res.data?.items)
  }

  const _bulkCreate = async () => {
    await Promise.all(
      _.map(dataForCreate, async (v) => {
        const createData = {
          ...v,
          report_type_id: id,
          default_value_type: v?.default_value_type || 'None',
          row_size: v?.row_size || 1,
        }

        const res = await createReportTypeAttributeApi(createData)

        if (res.status === 'error') {
          notification.error({
            message: 'Failed',
            description: res.message,
          })

          return
        }

        return res
      })
    )
  }

  const _bulkUpdate = async () => {
    await Promise.all(
      _.map(dataForUpdate, async (v) => {
        const updateData = {
          ...v,
          report_type_id: id,
        }

        const res = await updateReportTypeAttributeByIdApi(v.report_type_attribute_id, updateData)

        if (res.status === 'error') {
          notification.error({
            message: 'Failed',
            description: res.message,
          })

          return
        }

        return res
      })
    )
  }

  const _bulkCreateAndUpdate = async () => {
    await Promise.all([_bulkCreate(), _bulkUpdate()])
  }

  const _delete = async (id) => {
    if (deleting) return

    setDeleting(true)

    const res = await deleteReportTypeAttributeByIdApi(id)

    setDeleting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()
  }

  useAsync(async () => {
    if (!_.isEmpty(dictionaries)) {
      const results = await _.reduce(
        dictionaries,
        async (result, dictionary) => {
          const res = await getDictionaryValueFilterListApi(dictionary.id)

          return {
            ...(await result),
            [dictionary.id]: res?.data,
          }
        },
        Promise.resolve({})
      )

      setAllDictionaryValues(results)
    }
  }, [dictionaries])

  useAsync(async () => {
    _list()
  }, [status, filters])

  useAsync(async () => {
    if (!id) {
      history.push('./')

      return
    }

    await getReportTypeById(id)
  }, [id])

  useAsync(async () => {
    await Promise.all([
      getGroupAttributes({ forceGet: true }),
      getDataTypes({ forceGet: true }),
      getDictionaries({ forceGet: true }),
      getDays({ forceGet: true }),
      getReportTypeAttributes({ forceGet: true }),
      getReportStatuses({ forceGet: true }),
    ])
  }, [])

  const setDataForCreateAndUpdate = ({ data, key, field, value }) => {
    if (data?.forCreate) {
      const newDataForCreate = { ...dataForCreate }

      delete data.forCreate

      newDataForCreate[key] = {
        ...data,
        [field]: value,
      }

      setDataForCreate(newDataForCreate)
    } else {
      const newDataForUpdate = { ...dataForUpdate }

      newDataForUpdate[key] = {
        ...data,
        [field]: value,
      }

      setDataForUpdate(newDataForUpdate)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeValue = useCallback(
    _.debounce(({ key, value, field }) => {
      const index = _.findIndex(currentDataSource, { key })

      const updatedDataSource = update(currentDataSource[index], {
        [field]: { $set: value },
      })

      const newCurrentDataSource = update(currentDataSource, {
        $splice: [[index, 1, updatedDataSource]],
      })

      setCurrentDataSource(newCurrentDataSource)

      setDataForCreateAndUpdate({
        data: currentDataSource[index],
        field,
        key,
        value,
      })
    }, 200),
    [currentDataSource]
  )

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'report_type_attribute_name',
      key: 'report_type_attribute_name',
      className: 'bold',
      render: (text, record) => {
        if (editMode) {
          return (
            <Input
              defaultValue={record?.report_type_attribute_name}
              onChange={(e) => {
                debouncedChangeValue({
                  key: record.key,
                  value: e.target.value,
                  field: 'report_type_attribute_name',
                })
              }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('Code'),
      dataIndex: 'report_type_attribute_code',
      key: 'report_type_attribute_code',
      render: (text, record) => {
        if (editMode) {
          return (
            <Input
              defaultValue={record?.report_type_attribute_code}
              onChange={(e) => {
                debouncedChangeValue({
                  key: record.key,
                  value: e.target.value,
                  field: 'report_type_attribute_code',
                })
              }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('Description'),
      dataIndex: 'report_type_attribute_description',
      key: 'report_type_attribute_description',
      render: (text, record) => {
        if (editMode) {
          return (
            <Input
              defaultValue={record?.report_type_attribute_description}
              onChange={(e) => {
                debouncedChangeValue({
                  key: record.key,
                  value: e.target.value,
                  field: 'report_type_attribute_description',
                })
              }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('Group Attribute'),
      dataIndex: 'attribute_group_id',
      key: 'attribute_group_id',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={_.map(groupAttributes, (groupAttribute) => {
                return {
                  label: groupAttribute?.name,
                  value: groupAttribute?.id,
                }
              })}
              // value={record?.attribute_group_id}
              defaultValue={record?.attribute_group_id}
              onChange={(value) => {
                const field = 'attribute_group_id'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        if (!text) return
        return _.find(groupAttributes, { id: text })?.name
      },
    },
    {
      title: t('Data Type'),
      dataIndex: 'data_type_id',
      key: 'data_type_id',
      className: 'background-grey',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={_.map(dataTypes, (dataType) => {
                return {
                  label: dataType?.name,
                  value: dataType?.id,
                }
              })}
              // value={record?.data_type_id}
              defaultValue={record?.data_type_id}
              onChange={(value) => {
                const field = 'data_type_id'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        if (!text) return
        return _.find(dataTypes, { id: text })?.name
      },
    },
    {
      title: t('Dictionary'),
      dataIndex: 'dictionary_id',
      key: 'dictionary_id',
      className: 'background-grey',
      render: (text, record) => {
        if (editMode) {
          const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
          switch (dataTypeName) {
            case 'dictionary':
              return (
                <Select
                  options={_.map(dictionaries, (dictionary) => {
                    return {
                      label: dictionary?.name,
                      value: dictionary?.id,
                    }
                  })}
                  value={record?.dictionary_id}
                  onChange={(value) => {
                    const field = 'dictionary_id'
                    const key = record?.key

                    const index = _.findIndex(currentDataSource, { key })

                    const updatedDataSource = update(currentDataSource[index], {
                      [field]: { $set: value },
                      default_dictionary_value_id: { $set: null },
                    })

                    const newCurrentDataSource = update(currentDataSource, {
                      $splice: [[index, 1, updatedDataSource]],
                    })

                    setCurrentDataSource(newCurrentDataSource)

                    setDataForCreateAndUpdate({
                      data: currentDataSource[index],
                      field,
                      key,
                      value,
                    })
                  }}
                  style={{ width: '100%' }}
                />
              )

            default:
              return <Typography.Text>{t('None')}</Typography.Text>
          }
        }

        const dictionaryName = _.find(dictionaries, { id: text })?.name

        if (!dictionaryName) {
          return <Typography.Text>{t('None')}</Typography.Text>
        }

        return dictionaryName
      },
    },
    {
      title: t('Key'),
      dataIndex: 'is_key',
      key: 'is_key',
      className: 'background-blue',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Yes'), value: true },
                { label: t('No'), value: false },
              ]}
              // value={record?.is_key}
              defaultValue={record?.is_key}
              onChange={(value) => {
                const field = 'is_key'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text ? t('Yes') : t('No')
      },
    },
    {
      title: t('Weight'),
      dataIndex: 'key_weight',
      key: 'key_weight',
      className: 'background-blue',
      render: (text, record) => {
        if (editMode) {
          return (
            <InputNumber
              defaultValue={record?.key_weight}
              onChange={(value) => {
                debouncedChangeValue({
                  key: record.key,
                  value,
                  field: 'key_weight',
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('Status'),
      dataIndex: 'is_active',
      key: 'is_active',
      className: 'is-status',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Active'), value: true },
                { label: t('Inactive'), value: false },
              ]}
              // value={record?.is_active}
              defaultValue={record?.is_active}
              onChange={(value) => {
                const field = 'is_active'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return (
          <div
            className={classNames('status-block', {
              'is-active': record.is_active,
              'is-inactive': !record.is_active,
            })}
          >
            <Typography.Text>{record?.is_active ? t('Active') : t('Inactive')}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Default Value'),
      dataIndex: 'defaultValue',
      key: 'defaultValue',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'text':
            return (
              <Input
                disabled={!editMode}
                defaultValue={record?.default_value_str}
                onChange={(e) => {
                  debouncedChangeValue({
                    key: record.key,
                    value: e.target.value,
                    field: 'default_value_str',
                  })
                }}
              />
            )

          case 'integer':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.default_value_int}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'default_value_int',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          case 'float':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.default_value_int}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'default_value_int',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          case 'dictionary': {
            const dictionaryValues = allDictionaryValues?.[record?.dictionary_id]
            return (
              <Select
                disabled={!editMode}
                options={_.map(dictionaryValues, (dictionaryValue) => {
                  return {
                    label: dictionaryValue?.name,
                    value: dictionaryValue?.id,
                  }
                })}
                value={record?.default_dictionary_value_id}
                onChange={(value) => {
                  const field = 'default_dictionary_value_id'
                  const key = record?.key

                  const index = _.findIndex(currentDataSource, { key })

                  const updatedDataSource = update(currentDataSource[index], {
                    [field]: { $set: value },
                  })

                  const newCurrentDataSource = update(currentDataSource, {
                    $splice: [[index, 1, updatedDataSource]],
                  })

                  setCurrentDataSource(newCurrentDataSource)

                  setDataForCreateAndUpdate({
                    data: currentDataSource[index],
                    field,
                    key,
                    value,
                  })
                }}
                style={{ width: '100%' }}
              />
            )
          }

          case 'date':
            return (
              <Space size='small' direction='vertical' style={{ width: '100%' }}>
                <Select
                  disabled={!editMode}
                  options={[
                    { label: 'None', value: 'None' },
                    { label: 'Day', value: 'day' },
                  ]}
                  value={record?.default_value_type}
                  onChange={(value) => {
                    const field = 'default_value_type'
                    const key = record?.key

                    const index = _.findIndex(currentDataSource, { key })

                    const updatedDataSource = update(currentDataSource[index], {
                      [field]: { $set: value },
                      default_day_id: {
                        $set: value === 'None' ? null : record?.default_day_id,
                      },
                    })

                    const newCurrentDataSource = update(currentDataSource, {
                      $splice: [[index, 1, updatedDataSource]],
                    })

                    setCurrentDataSource(newCurrentDataSource)

                    setDataForCreateAndUpdate({
                      data: currentDataSource[index],
                      field,
                      key,
                      value,
                    })
                  }}
                  style={{ width: '100%' }}
                />

                {record?.default_value_type === 'None' && (
                  <Select disabled style={{ width: '100%' }} />
                )}

                {record?.default_value_type === 'day' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(days, (day) => {
                      return {
                        label: day?.name,
                        value: day?.id,
                      }
                    })}
                    value={record?.default_day_id}
                    onChange={(value) => {
                      const field = 'default_day_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}
              </Space>
            )

          case 'calculation':
            return <Typography.Text>{t('None')}</Typography.Text>

          case 'attachment':
            return <Typography.Text>{t('None')}</Typography.Text>

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('min length'),
      dataIndex: 'minLength',
      key: 'minLength',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'text':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.min_length}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'min_length',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('max length'),
      dataIndex: 'maxLength',
      key: 'maxLength',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'text':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.max_length}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'max_length',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('min quantity'),
      dataIndex: 'minQuantity',
      key: 'minQuantity',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'float':
          case 'integer':
            return (
              <Space size='small' direction='vertical' style={{ width: '100%' }}>
                <Select
                  disabled={!editMode}
                  options={[
                    { label: 'None', value: 'None' },
                    { label: 'Direct', value: 'Direct' },
                    { label: 'Attribute', value: 'Attribute' },
                  ]}
                  value={record?.min_qty_type}
                  onChange={(value) => {
                    const field = 'min_qty_type'
                    const key = record?.key

                    const index = _.findIndex(currentDataSource, { key })

                    const updatedDataSource = update(currentDataSource[index], {
                      [field]: { $set: value },
                    })

                    const newCurrentDataSource = update(currentDataSource, {
                      $splice: [[index, 1, updatedDataSource]],
                    })

                    setCurrentDataSource(newCurrentDataSource)

                    setDataForCreateAndUpdate({
                      data: currentDataSource[index],
                      field,
                      key,
                      value,
                    })
                  }}
                  style={{ width: '100%' }}
                />

                {record?.min_qty_type === 'None' && <Select disabled style={{ width: '100%' }} />}

                {record?.min_qty_type === 'Direct' && (
                  <InputNumber
                    disabled={!editMode}
                    defaultValue={record?.min_qty_value}
                    onChange={(value) => {
                      debouncedChangeValue({
                        key: record.key,
                        value,
                        field: 'min_qty_value',
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}

                {record?.min_qty_type === 'Attribute' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(
                      _.filter(reportTypeAttributes, (reportTypeAttribute) => {
                        return (
                          _.includes(numberDataTypesIds, reportTypeAttribute.data_type_id) &&
                          reportTypeAttribute.report_type_id === Number(id)
                        )
                      }),
                      (reportTypeAttribute) => {
                        return {
                          label: reportTypeAttribute?.report_type_attribute_name,
                          value: reportTypeAttribute?.report_type_attribute_id,
                        }
                      }
                    )}
                    value={record?.min_qty_attribute_id}
                    onChange={(value) => {
                      const field = 'min_qty_attribute_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}
              </Space>
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('max quantity'),
      dataIndex: 'maxQuantity',
      key: 'maxQuantity',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'float':
          case 'integer':
            return (
              <Space size='small' direction='vertical' style={{ width: '100%' }}>
                <Select
                  disabled={!editMode}
                  options={[
                    { label: 'None', value: 'None' },
                    { label: 'Direct', value: 'Direct' },
                    { label: 'Attribute', value: 'Attribute' },
                  ]}
                  value={record?.max_qty_type}
                  onChange={(value) => {
                    const field = 'max_qty_type'
                    const key = record?.key

                    const index = _.findIndex(currentDataSource, { key })

                    const updatedDataSource = update(currentDataSource[index], {
                      [field]: { $set: value },
                    })

                    const newCurrentDataSource = update(currentDataSource, {
                      $splice: [[index, 1, updatedDataSource]],
                    })

                    setCurrentDataSource(newCurrentDataSource)

                    setDataForCreateAndUpdate({
                      data: currentDataSource[index],
                      field,
                      key,
                      value,
                    })
                  }}
                  style={{ width: '100%' }}
                />

                {record?.max_qty_type === 'None' && <Select disabled style={{ width: '100%' }} />}

                {record?.max_qty_type === 'Direct' && (
                  <InputNumber
                    disabled={!editMode}
                    defaultValue={record?.max_qty_value}
                    onChange={(value) => {
                      debouncedChangeValue({
                        key: record.key,
                        value,
                        field: 'max_qty_value',
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}

                {record?.max_qty_type === 'Attribute' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(
                      _.filter(reportTypeAttributes, (reportTypeAttribute) => {
                        return (
                          _.includes(numberDataTypesIds, reportTypeAttribute.data_type_id) &&
                          reportTypeAttribute.report_type_id === Number(id)
                        )
                      }),
                      (reportTypeAttribute) => {
                        return {
                          label: reportTypeAttribute?.report_type_attribute_name,
                          value: reportTypeAttribute?.report_type_attribute_id,
                        }
                      }
                    )}
                    value={record?.max_qty_attribute_id}
                    onChange={(value) => {
                      const field = 'max_qty_attribute_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}
              </Space>
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('max decimal'),
      dataIndex: 'maxDecimal',
      key: 'maxDecimal',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'float':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.max_decimal}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'max_decimal',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('Significant Value'),
      dataIndex: 'significantValue',
      key: 'significantValue',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'integer':
          case 'float':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.significant_qty}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'significant_qty',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('min date'),
      dataIndex: 'minDate',
      key: 'minDate',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'date':
            return (
              <Space size='small' direction='vertical' style={{ width: '100%' }}>
                <Select
                  disabled={!editMode}
                  options={[
                    { label: 'None', value: 'None' },
                    { label: 'Day', value: 'day' },
                    { label: 'Attribute', value: 'attribute' },
                  ]}
                  value={record?.min_date_type}
                  onChange={(value) => {
                    const field = 'min_date_type'
                    const key = record?.key

                    const index = _.findIndex(currentDataSource, { key })

                    const updatedDataSource = update(currentDataSource[index], {
                      [field]: { $set: value },
                    })

                    const newCurrentDataSource = update(currentDataSource, {
                      $splice: [[index, 1, updatedDataSource]],
                    })

                    setCurrentDataSource(newCurrentDataSource)

                    setDataForCreateAndUpdate({
                      data: currentDataSource[index],
                      field,
                      key,
                      value,
                    })
                  }}
                  style={{ width: '100%' }}
                />

                {record?.min_date_type === 'None' && <Select disabled style={{ width: '100%' }} />}

                {record?.min_date_type === 'day' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(days, (day) => {
                      return {
                        label: day?.name,
                        value: day?.id,
                      }
                    })}
                    value={record?.min_day_id}
                    onChange={(value) => {
                      const field = 'min_day_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}

                {record?.min_date_type === 'attribute' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(
                      _.filter(reportTypeAttributes, {
                        data_type_id: record.data_type_id,
                        report_type_id: Number(id),
                      }),
                      (reportTypeAttribute) => {
                        return {
                          label: reportTypeAttribute?.report_type_attribute_name,
                          value: reportTypeAttribute?.report_type_attribute_id,
                        }
                      }
                    )}
                    value={record?.min_date_attribute_id}
                    onChange={(value) => {
                      const field = 'min_date_attribute_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}
              </Space>
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('max date'),
      dataIndex: 'maxDate',
      key: 'maxDate',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'date':
            return (
              <Space size='small' direction='vertical' style={{ width: '100%' }}>
                <Select
                  disabled={!editMode}
                  options={[
                    { label: 'None', value: 'None' },
                    { label: 'Day', value: 'day' },
                    { label: 'Attribute', value: 'attribute' },
                  ]}
                  value={record?.max_date_type}
                  onChange={(value) => {
                    const field = 'max_date_type'
                    const key = record?.key

                    const index = _.findIndex(currentDataSource, { key })

                    const updatedDataSource = update(currentDataSource[index], {
                      [field]: { $set: value },
                    })

                    const newCurrentDataSource = update(currentDataSource, {
                      $splice: [[index, 1, updatedDataSource]],
                    })

                    setCurrentDataSource(newCurrentDataSource)

                    setDataForCreateAndUpdate({
                      data: currentDataSource[index],
                      field,
                      key,
                      value,
                    })
                  }}
                  style={{ width: '100%' }}
                />

                {record?.max_date_type === 'None' && <Select disabled style={{ width: '100%' }} />}

                {record?.max_date_type === 'day' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(days, (day) => {
                      return {
                        label: day?.name,
                        value: day?.id,
                      }
                    })}
                    value={record?.max_day_id}
                    onChange={(value) => {
                      const field = 'max_day_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}

                {record?.max_date_type === 'attribute' && (
                  <Select
                    disabled={!editMode}
                    options={_.map(
                      _.filter(reportTypeAttributes, {
                        data_type_id: record.data_type_id,
                        report_type_id: Number(id),
                      }),
                      (reportTypeAttribute) => {
                        return {
                          label: reportTypeAttribute?.report_type_attribute_name,
                          value: reportTypeAttribute?.report_type_attribute_id,
                        }
                      }
                    )}
                    value={record?.max_date_attribute_id}
                    onChange={(value) => {
                      const field = 'max_date_attribute_id'
                      const key = record?.key

                      const index = _.findIndex(currentDataSource, { key })

                      const updatedDataSource = update(currentDataSource[index], {
                        [field]: { $set: value },
                      })

                      const newCurrentDataSource = update(currentDataSource, {
                        $splice: [[index, 1, updatedDataSource]],
                      })

                      setCurrentDataSource(newCurrentDataSource)

                      setDataForCreateAndUpdate({
                        data: currentDataSource[index],
                        field,
                        key,
                        value,
                      })
                    }}
                    style={{ width: '100%' }}
                  />
                )}
              </Space>
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('File size(mb)'),
      dataIndex: 'fileSize',
      key: 'fileSize',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'attachment':
            return (
              <InputNumber
                disabled={!editMode}
                defaultValue={record?.max_size}
                onChange={(value) => {
                  debouncedChangeValue({
                    key: record.key,
                    value,
                    field: 'max_size',
                  })
                }}
                style={{ width: '100%' }}
              />
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('Equotion'),
      dataIndex: 'equotion',
      key: 'equotion',
      className: 'background-grey',
      render: (text, record) => {
        const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name
        switch (dataTypeName) {
          case 'calculation':
            return (
              <Input
                disabled={!editMode}
                defaultValue={record?.cal_equation}
                onChange={(e) => {
                  debouncedChangeValue({
                    key: record.key,
                    value: e.target.value,
                    field: 'cal_equation',
                  })
                }}
              />
            )

          default:
            return <Typography.Text>{t('None')}</Typography.Text>
        }
      },
    },
    {
      title: t('nullable'),
      dataIndex: 'is_nullable',
      key: 'is_nullable',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Yes'), value: true },
                { label: t('No'), value: false },
              ]}
              // value={record?.is_nullable}
              defaultValue={record?.is_nullable}
              onChange={(value) => {
                const field = 'is_nullable'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text ? t('Yes') : t('No')
      },
    },
    {
      title: t('nullable(RO)'),
      dataIndex: 'is_nullable_ro',
      key: 'is_nullable_ro',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Yes'), value: true },
                { label: t('No'), value: false },
              ]}
              // value={record?.is_nullable_ro}
              defaultValue={record?.is_nullable_ro}
              onChange={(value) => {
                const field = 'is_nullable_ro'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text ? t('Yes') : t('No')
      },
    },
    {
      title: t('stage'),
      dataIndex: 'report_status_id',
      key: 'report_status_id',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              disabled={!editMode}
              options={_.map(reportStatuses, (reportStatus) => {
                return {
                  label: reportStatus?.name,
                  value: reportStatus?.id,
                }
              })}
              value={record?.report_status_id}
              onChange={(value) => {
                const field = 'report_status_id'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        const reportStatusName = _.find(reportStatuses, { id: text })?.name

        if (!reportStatusName) {
          return <Typography.Text>{t('None')}</Typography.Text>
        }

        return reportStatusName
      },
    },
    {
      title: t('line'),
      dataIndex: 'report_line',
      key: 'report_line',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <InputNumber
              defaultValue={record?.report_line}
              onChange={(value) => {
                debouncedChangeValue({
                  key: record.key,
                  value,
                  field: 'report_line',
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('order'),
      dataIndex: 'report_order',
      key: 'report_order',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <InputNumber
              defaultValue={record?.report_order}
              onChange={(value) => {
                debouncedChangeValue({
                  key: record.key,
                  value,
                  field: 'report_order',
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('row size'),
      dataIndex: 'row_size',
      key: 'row_size',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <InputNumber
              defaultValue={record?.row_size}
              onChange={(value) => {
                debouncedChangeValue({
                  key: record.key,
                  value,
                  field: 'row_size',
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text
      },
    },
    {
      title: t('dashboard'),
      dataIndex: 'is_dashboard',
      key: 'is_dashboard',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Yes'), value: true },
                { label: t('No'), value: false },
              ]}
              // value={record?.is_dashboard}
              defaultValue={record?.is_dashboard}
              onChange={(value) => {
                const field = 'is_dashboard'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text ? t('Yes') : t('No')
      },
    },
    {
      title: t('description'),
      dataIndex: 'is_description',
      key: 'is_description',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Yes'), value: true },
                { label: t('No'), value: false },
              ]}
              // value={record?.is_description}
              defaultValue={record?.is_description}
              onChange={(value) => {
                const field = 'is_description'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text ? t('Yes') : t('No')
      },
    },
    {
      title: t('email'),
      dataIndex: 'is_daily_email',
      key: 'is_daily_email',
      className: 'background-yellow',
      render: (text, record) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Yes'), value: true },
                { label: t('No'), value: false },
              ]}
              // value={record?.is_daily_email}
              defaultValue={record?.is_daily_email}
              onChange={(value) => {
                const field = 'is_daily_email'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                setDataForCreateAndUpdate({
                  data: currentDataSource[index],
                  field,
                  key,
                  value,
                })
              }}
              style={{ width: '100%' }}
            />
          )
        }

        return text ? t('Yes') : t('No')
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Delete')}>
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const deletingDataSource = _.find(currentDataSource, {
                    report_type_attribute_id: record?.report_type_attribute_id,
                  })

                  ModalAntd.confirm({
                    title: `Confirm deletion: ${reportType?.name}`,
                    icon: null,
                    maskClosable: true,
                    closeIcon: true,
                    content: (
                      <div>
                        <dl>
                          <dt>Code</dt>
                          <dd>- {deletingDataSource?.report_type_attribute_code}</dd>
                          <dt>Name</dt>
                          <dd>- {deletingDataSource?.report_type_attribute_name}</dd>
                        </dl>
                      </div>
                    ),
                    okButtonProps: {
                      shape: 'round',
                    },
                    okText: 'Delete',
                    okType: 'primary',
                    cancelButtonProps: {
                      shape: 'round',
                    },
                    cancelText: 'Cancel',
                    onOk: async () => {
                      await _delete(record?.report_type_attribute_id)
                    },
                  })
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  const getChangeMessages = () => {
    const result = ''
    return result
  }

  const changeMessages = getChangeMessages()

  return (
    <Styled className='page page-report-attribute-detail'>
      <Page
        title={t('Report Attribute')}
        searchPlaceholder={t('Report Attribute or description')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
            page: 1,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between' align='middle'>
            <Col>
              {/* <Space size='middle'> */}
              <Button icon={<LeftOutlined />} onClick={() => history.push('./')}>
                {t('Back')}
              </Button>

              {/* </Space> */}
            </Col>
            <Col>
              <FilterGroupAttributeSelect
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    group_attribute_id: value,
                    page: 1,
                  })
                }}
              />
            </Col>
            <Col>
              <FilterDataTypeSelect
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    data_type_id: value,
                    page: 1,
                  })
                }}
              />
            </Col>
            <Col>
              <Select
                options={[
                  { label: t('Yes'), value: true },
                  { label: t('No'), value: false },
                ]}
                placeholder={t('key attribute')}
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    key_attribute: value,
                    page: 1,
                  })
                }}
              />
            </Col>

            <Col>
              {/* <Space> */}
              <Row>
                <Col>
                  <Button
                    disabled={!editMode}
                    type='primary'
                    size='large'
                    onClick={() => {
                      setTotal(total + 1)
                      setCurrentDataSource([
                        {
                          key: uuidv4(),
                          forCreate: true,
                        },
                        ...currentDataSource,
                      ])
                    }}
                  >
                    {t('Create New Report Attribute')}
                  </Button>
                </Col>
                <Col>
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => {
                      setShowReportPreview(true)
                    }}
                  >
                    {t('Report Preview')}
                  </Button>
                </Col>
              </Row>

              {/* </Space> */}
            </Col>
          </Row>

          <Card
            title={reportType?.name}
            // titleExtra={

            // }
          >
            <Row justify='space-between' gutter={[2, 6]}>
              <Col xs={24} sm={8} lg={4}>
                {editMode ? (
                  <Space>
                    <Button
                      type='primary'
                      onClick={() => {
                        setShowConfirmModal(true)
                      }}
                    >
                      {t('Save')}
                    </Button>
                    <Button
                      onClick={() => {
                        setCurrentDataSource(oldDataSource)
                        setEditMode(false)
                      }}
                    >
                      {t('Cancel')}
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type='primary'
                    onClick={() => {
                      setCurrentDataSource(
                        _.map(currentDataSource, (v) => {
                          return {
                            ...v,
                            editMode,
                          }
                        })
                      )
                      setEditMode(true)
                    }}
                  >
                    {t('Edit Mode')}
                  </Button>
                )}
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={loading}
              columns={columns}
              dataSource={currentDataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 4000 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        visible={showConfirmModal}
        title={t('Confirm changes')}
        onCancel={() => {
          setShowConfirmModal(false)
        }}
        footer={[
          <Button
            loading={submitting}
            key='save'
            type='primary'
            onClick={async () => {
              if (submitting) return

              setSubmitting(false)

              await _bulkCreateAndUpdate()

              setSubmitting(false)

              _list()

              setEditMode(false)
              setShowConfirmModal(false)
              setDataForCreate({})
              setDataForUpdate({})
            }}
          >
            {t('Save')}
          </Button>,
        ]}
      >
        <div>
          <Typography.Text>{t('You have change report attribute as below')}</Typography.Text>
          <Typography.Paragraph>
            <ol>
              {_.map(changeMessages, (changeMessage, index) => {
                return (
                  <li key={index}>
                    <Typography.Text>{changeMessage}</Typography.Text>
                  </li>
                )
              })}
            </ol>
          </Typography.Paragraph>
        </div>
      </Modal>

      <Modal
        width={800}
        visible={showReportPreview}
        title={`${reportType?.name}`}
        footer={null}
        destroyOnClose
        onCancel={() => {
          setShowReportPreview(false)
        }}
      >
        <div>
          <ReportPreview id={id} />
        </div>
      </Modal>
    </Styled>
  )
}
